import * as React from 'react';
import * as Yup from 'yup';
import { styled, alpha } from '@mui/material/styles';
import { Backdrop, Divider, Fade, FormControlLabel, Popper, Radio, RadioGroup } from '@mui/material';
import Menu, { MenuProps } from '@mui/material/Menu';
import { Paper, Stack } from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import TroubleshootIcon from '@mui/icons-material/Troubleshoot';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import ButtonLoading from '@mui/lab/LoadingButton';
import Button from '@mui/material/Button';
import { useGetSubjectDetails } from 'services/v1/SystemTenant/AROKMS/SubjectService';
import { useGetTableAnalyticOptions } from 'services/v1/SystemTenant/AROKMS/TableAnalyticService';
import { useFormik } from 'formik';
import { AutoCompleteItem } from 'types/api/Common/AutoCompleteTypes';
import { useParams } from 'react-router-dom';

const StyledPopper = styled(Popper)(() => ({
  zIndex: 1000,
}));

export const StyledMenu = styled((props: MenuProps) => (
  <Menu
    elevation={0}
    anchorOrigin={{
      vertical: 'bottom',
      horizontal: 'right',
    }}
    transformOrigin={{
      vertical: 'top',
      horizontal: 'right',
    }}
    {...props}
  />
))(({ theme }) => ({
  '& .MuiPaper-root': {
    borderRadius: 6,
    marginTop: theme.spacing(1),
    minWidth: 180,
    color: theme.palette.mode === 'light' ? 'rgb(55, 65, 81)' : theme.palette.grey[300],
    boxShadow:
      'rgb(255, 255, 255) 0px 0px 0px 0px, rgba(0, 0, 0, 0.05) 0px 0px 0px 1px, rgba(0, 0, 0, 0.1) 0px 10px 15px -3px, rgba(0, 0, 0, 0.05) 0px 4px 6px -2px',
    '& .MuiMenu-list': {
      padding: '4px 0',
    },
    '& .MuiMenuItem-root': {
      color: '#42BB93',
      fontSize: 13,
      '& .MuiSvgIcon-root': {
        fontSize: 16,
        color: theme.palette.text.secondary,
        marginRight: theme.spacing(1.5),
      },

      '&:active': {
        backgroundColor: alpha(theme.palette.primary.main, theme.palette.action.selectedOpacity),
      },
    },
  },
}));

export enum FileType {
  CSV = 'CSV',
  EXCEL = 'EXCEL',
}

export interface FormTableAnalyticParameter {
  rowColumn: AutoCompleteItem | null;
  columnColumn: AutoCompleteItem | null;
  dataColumn: AutoCompleteItem | null;
  dataDecimals: string;
  scale?: string;
}

interface ButtonTableAnalyticsProps {
  onDisplayData?: (data: FormTableAnalyticParameter) => void;
  disabled?: boolean | undefined;
}

const autocompleteStyles = {
  '& .MuiOutlinedInput-root': {
    padding: '4px 8px',
    fontSize: '13px',
    borderRadius: '8px',
    backgroundColor: '#f9fafb',
    transition: 'background-color 0.2s ease',
    '&:hover': {
      backgroundColor: '#f3f4f6',
    },
    '&.Mui-focused': {
      backgroundColor: '#fff',
    },
  },
  '& .MuiAutocomplete-popper': {
    fontSize: '13px',
  },
};

const textInputStyles = {
  '& .MuiOutlinedInput-root': {
    padding: '0px',
    fontSize: '13px',
    borderRadius: '8px',
    backgroundColor: '#f9fafb',
    transition: 'background-color 0.2s ease',
    '&:hover': {
      backgroundColor: '#f3f4f6',
    },
    '&.Mui-focused': {
      backgroundColor: '#fff',
    },
  },
};

const radioButtonLabelStyle = {
  '& .MuiFormControlLabel-label': {
    fontSize: '13px',
    color: '#667085',
  },
};

const radioButtonStyle = {
  color: '#98A2AE',
  padding: '4px',
  '& .MuiSvgIcon-root': {
    fontSize: 18,
  },
  '&.Mui-checked': {
    color: '#42BB93',
  },
};

export const TABLE_ANALYTIC_SCALE_OPTION_VALUE = {
  THOUSAND: 'thousand',
  MILLION: 'MILLION',
};

export const TABLE_ANALYTIC_SCALE_ABBREVIATION = new Map([
  [TABLE_ANALYTIC_SCALE_OPTION_VALUE.THOUSAND, 'K'],
  [TABLE_ANALYTIC_SCALE_OPTION_VALUE.MILLION, 'M'],
]);

export const TABLE_ANALYTICS_SCALE_OPTIONS = [
  {
    label: 'None',
    value: 'NONE',
  },
  {
    label: 'Thousand',
    value: TABLE_ANALYTIC_SCALE_OPTION_VALUE.THOUSAND,
  },
  {
    label: 'Million',
    value: TABLE_ANALYTIC_SCALE_OPTION_VALUE.MILLION,
  },
];

export const tableAnalyticParameterValidatonSchema = Yup.object().shape({
  rowColumn: Yup.object().required('Required'),
  columnColumn: Yup.object().required('Required'),
  dataColumn: Yup.object().required('Required'),
  dataDecimals: Yup.string().required('Required'),
});

const formikInitialValues: FormTableAnalyticParameter = {
  rowColumn: null,
  columnColumn: null,
  dataColumn: {
    label: 'Count',
    value: 'COUNT',
  },
  dataDecimals: '0',
  scale: 'NONE',
};

const fieldLabelStyles = {
  fontSize: '13px',
  fontWeight: 700,
  color: '#344054',
  minWidth: 95,
};

export function ButtonTableAnalytics(props: ButtonTableAnalyticsProps) {
  const { onDisplayData, disabled } = props;
  const { subjectId } = useParams<{ subjectId: string }>();
  const { data: subjectDetails } = useGetSubjectDetails(subjectId);

  const { data: tableAnalyticOptions } = useGetTableAnalyticOptions({
    enable: true,
    subjectId: subjectDetails?.data?.id?.toString(),
  });

  const [anchorEl, setAnchorEl] = React.useState<HTMLElement | null>(null);

  const handlePopoverOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handlePopoverClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);

  const formik = useFormik<FormTableAnalyticParameter>({
    initialValues: formikInitialValues,
    validationSchema: tableAnalyticParameterValidatonSchema,
    onSubmit: (values, formikHelper) => {
      handlePopoverClose();
      onDisplayData &&
        onDisplayData({
          ...values,
          scale: values.scale === 'NONE' ? undefined : values.scale,
        });
      formikHelper.resetForm();
    },
  });

  const isFormValid = tableAnalyticParameterValidatonSchema.isValidSync(formik.values);

  const rowOptions = React.useMemo(() => {
    if (tableAnalyticOptions?.data?.rowOptions) {
      return tableAnalyticOptions?.data?.rowOptions.filter(
        (option) => option.value !== formik.values.columnColumn?.value
      );
    }
    return [];
  }, [tableAnalyticOptions?.data?.rowOptions, formik.values.columnColumn]);

  const columnOptions = React.useMemo(() => {
    if (tableAnalyticOptions?.data?.columnOptions) {
      return tableAnalyticOptions?.data?.columnOptions.filter(
        (option) => option.value !== formik.values.rowColumn?.value
      );
    }
    return [];
  }, [tableAnalyticOptions?.data?.columnOptions, formik.values.rowColumn]);

  const handleReset = () => {
    formik.resetForm();
  };

  return (
    <div>
      <Backdrop open={open} sx={{ zIndex: 999, backgroundColor: 'rgba(0, 0, 0, 0.25)' }} />
      <Tooltip title='Table Analytics'>
        <span data-testid='button-export-label'>
          <IconButton data-testid='button-export' onClick={handlePopoverOpen}>
            <TroubleshootIcon sx={{ color: '#42BB93' }} />
          </IconButton>
        </span>
      </Tooltip>

      <StyledPopper
        open={open}
        anchorEl={anchorEl}
        placement='bottom'
        transition
        modifiers={[
          {
            name: 'flip',
            enabled: true,
            options: {
              altBoundary: true,
              rootBoundary: 'document',
              padding: 8,
            },
          },
          {
            name: 'preventOverflow',
            enabled: true,
            options: {
              altAxis: true,
              altBoundary: true,
              tether: true,
              rootBoundary: 'document',
              padding: 8,
            },
          },
          {
            name: 'offset',
            options: {
              offset: [0, 8],
            },
          },
        ]}
      >
        {({ TransitionProps }) => (
          <Fade {...TransitionProps} timeout={200}>
            <Paper
              sx={{
                py: 2.5,
                px: 3,
                display: 'flex',
                flexDirection: 'column',
                borderRadius: '12px',
                boxShadow: '0px 8px 24px rgba(0, 0, 0, 0.12), 0px 2px 8px rgba(0, 0, 0, 0.08)',
                minWidth: 380,
              }}
            >
              {/* Header */}
              <Stack direction='row' alignItems='center' gap={1} mb={0.5}>
                <TroubleshootIcon sx={{ color: '#42BB93', fontSize: 22 }} />
                <Typography fontWeight='bold' sx={{ fontSize: '16px', color: '#1D2939' }}>
                  Table Analytics
                </Typography>
              </Stack>
              <Typography sx={{ fontSize: '12px', color: '#667085', mb: 2 }}>
                Configure pivot dimensions to analyze your data
              </Typography>
              <Divider sx={{ mb: 2.5 }} />

              {/* Column Field */}
              <Stack mb={2}>
                <Typography sx={fieldLabelStyles} mb={0.5}>
                  Column
                </Typography>
                <Autocomplete
                  options={columnOptions}
                  value={formik.values.columnColumn}
                  sx={autocompleteStyles}
                  onChange={(_event, newValue) => {
                    formik.setFieldValue('columnColumn', newValue);
                  }}
                  clearIcon={null}
                  renderInput={(params) => <TextField {...params} placeholder='Select column attribute...' />}
                />
              </Stack>

              {/* Row Field */}
              <Stack mb={2}>
                <Typography sx={fieldLabelStyles} mb={0.5}>
                  Row
                </Typography>
                <Autocomplete
                  options={rowOptions}
                  value={formik.values.rowColumn}
                  sx={autocompleteStyles}
                  onChange={(_event, newValue) => {
                    formik.setFieldValue('rowColumn', newValue);
                  }}
                  clearIcon={null}
                  renderInput={(params) => <TextField {...params} placeholder='Select row attribute...' />}
                />
              </Stack>

              {/* Data Field */}
              <Stack mb={2}>
                <Typography sx={fieldLabelStyles} mb={0.5}>
                  Data
                </Typography>
                <Autocomplete
                  options={tableAnalyticOptions?.data?.dataOptions || []}
                  value={formik.values.dataColumn}
                  sx={autocompleteStyles}
                  onChange={(_event, newValue) => {
                    formik.setFieldValue('dataColumn', newValue);
                  }}
                  clearIcon={null}
                  renderInput={(params) => <TextField {...params} placeholder='Select data metric...' />}
                />
              </Stack>

              {/* Data Decimals & Scale Row */}
              <Stack direction='row' gap={2} mb={2.5} alignItems='flex-start'>
                <Stack flex={1}>
                  <Typography sx={fieldLabelStyles} mb={0.5}>
                    Decimals
                  </Typography>
                  <TextField
                    sx={textInputStyles}
                    name='dataDecimals'
                    hiddenLabel
                    value={formik.values.dataDecimals}
                    onChange={(e) => {
                      if (/^[0-9]*$/.test(e.target.value)) {
                        formik.handleChange(e);
                      }
                    }}
                    onBlur={formik.handleBlur}
                    variant='outlined'
                    size='small'
                    placeholder='e.g. 3'
                    inputProps={{ style: { textAlign: 'center' } }}
                  />
                </Stack>
                <Stack flex={2}>
                  <Typography sx={fieldLabelStyles} mb={0.5}>
                    Scale
                  </Typography>
                  <RadioGroup
                    value={formik.values.scale}
                    onChange={(_event, value) => {
                      formik.setFieldValue('scale', value);
                      if (value === TABLE_ANALYTIC_SCALE_OPTION_VALUE.MILLION) {
                        formik.setFieldValue('dataDecimals', '2');
                      }
                    }}
                  >
                    <Stack direction='row' spacing={0.5}>
                      {TABLE_ANALYTICS_SCALE_OPTIONS.map((option) => (
                        <FormControlLabel
                          key={option.value}
                          value={option.value}
                          sx={radioButtonLabelStyle}
                          control={<Radio sx={radioButtonStyle} />}
                          label={option.label}
                        />
                      ))}
                    </Stack>
                  </RadioGroup>
                </Stack>
              </Stack>

              <Divider sx={{ mb: 2 }} />

              {/* Actions */}
              <Stack direction='row' spacing={1.5} justifyContent='flex-end' alignItems='center'>
                <Button
                  onClick={handleReset}
                  size='small'
                  sx={{
                    textTransform: 'none',
                    fontSize: '13px',
                    color: '#667085',
                    '&:hover': { backgroundColor: '#f3f4f6' },
                  }}
                >
                  Reset
                </Button>
                <Button onClick={handlePopoverClose} variant='main-table-panel-border' sx={{ minWidth: 80 }}>
                  Cancel
                </Button>
                <ButtonLoading
                  variant='main-table-panel'
                  onClick={() => formik.handleSubmit()}
                  disabled={!isFormValid || disabled}
                  sx={{ minWidth: 80 }}
                >
                  Display
                </ButtonLoading>
              </Stack>
            </Paper>
          </Fade>
        )}
      </StyledPopper>
    </div>
  );
}
